import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useOwners, useCreateOwner } from '@/hooks/useOwners';
import { useCreatePatient } from '@/hooks/usePatients';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import { ChevronDown, ChevronUp, UserPlus, Users } from 'lucide-react';

// Schema para propietario nuevo
const ownerSchema = z.object({
  first_name: z.string().min(1, 'El nombre es requerido'),
  last_name: z.string().min(1, 'El apellido es requerido'),
  phone: z.string().optional(),
  email: z.string().email('Email inválido').optional().or(z.literal('')),
  address: z.string().optional(),
});

// Schema para paciente
const patientSchema = z.object({
  name: z.string().min(1, 'El nombre es requerido'),
  species: z.string().min(1, 'La especie es requerida'),
  breed: z.string().optional(),
  color: z.string().optional(),
  sex: z.enum(['male', 'female', 'unknown']),
  age_years: z.union([z.string(), z.number()]).optional(),
  age_months: z.union([z.string(), z.number()]).optional(),
  weight: z.union([z.string(), z.number()]).optional(),
  microchip_number: z.string().optional(),
  notes: z.string().optional(),
  allergies: z.string().optional(),
});

type OwnerFormData = z.infer<typeof ownerSchema>;
type PatientFormData = z.infer<typeof patientSchema>;

interface PatientWithOwnerFormProps {
  onSuccess: () => void;
}

const speciesOptions = [
  { value: 'Canino', label: 'Canino' },
  { value: 'Felino', label: 'Felino' },
  { value: 'Otro', label: 'Otro' },
];

const sexOptions = [
  { value: 'male', label: 'Macho' },
  { value: 'female', label: 'Hembra' },
  { value: 'unknown', label: 'No especificado' },
];

// Función para calcular fecha de nacimiento desde edad
function calculateBirthDate(years?: number, months?: number): string | undefined {
  if (!years && !months) return undefined;
  const today = new Date();
  const birthDate = new Date(today);
  birthDate.setFullYear(birthDate.getFullYear() - (years || 0));
  birthDate.setMonth(birthDate.getMonth() - (months || 0));
  return birthDate.toISOString().split('T')[0];
}

export default function PatientWithOwnerForm({ onSuccess }: PatientWithOwnerFormProps) {
  const [ownerType, setOwnerType] = useState<'existing' | 'new'>('existing');
  const [selectedOwnerId, setSelectedOwnerId] = useState<number | null>(null);
  const [ownerSearch, setOwnerSearch] = useState('');
  const [showMorePatientFields, setShowMorePatientFields] = useState(false);
  const [showMoreOwnerFields, setShowMoreOwnerFields] = useState(false);

  const { data: owners } = useOwners(ownerSearch || undefined);
  const createOwner = useCreateOwner();
  const createPatient = useCreatePatient();

  // Form para paciente
  const patientForm = useForm<PatientFormData>({
    resolver: zodResolver(patientSchema),
    defaultValues: {
      sex: 'unknown',
      species: 'Perro',
    },
  });

  // Form para propietario nuevo
  const ownerForm = useForm<OwnerFormData>({
    resolver: zodResolver(ownerSchema),
    defaultValues: {},
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validar formulario de paciente
    const patientValid = await patientForm.trigger();
    if (!patientValid) return;

    let ownerId = selectedOwnerId;

    // Si es propietario nuevo, validar y crear
    if (ownerType === 'new') {
      const ownerValid = await ownerForm.trigger();
      if (!ownerValid) return;

      try {
        const ownerData = ownerForm.getValues();
        const cleanOwnerData = {
          first_name: ownerData.first_name,
          last_name: ownerData.last_name,
          phone: ownerData.phone || undefined,
          email: ownerData.email || undefined,
          address: ownerData.address || undefined,
        };
        const newOwner = await createOwner.mutateAsync(cleanOwnerData);
        ownerId = newOwner.id;
      } catch (error) {
        console.error('Error creating owner:', error);
        return;
      }
    }

    if (!ownerId) {
      alert('Debe seleccionar o crear un propietario');
      return;
    }

    // Crear paciente
    try {
      const patientData = patientForm.getValues();
      const birthDate = calculateBirthDate(
        patientData.age_years ? Number(patientData.age_years) : undefined,
        patientData.age_months ? Number(patientData.age_months) : undefined
      );

      const cleanPatientData = {
        name: patientData.name,
        species: patientData.species,
        sex: patientData.sex,
        owner_id: ownerId,
        breed: patientData.breed || undefined,
        color: patientData.color || undefined,
        birth_date: birthDate,
        weight: patientData.weight ? Number(patientData.weight) : undefined,
        microchip_number: patientData.microchip_number || undefined,
        notes: patientData.notes || undefined,
        allergies: patientData.allergies || undefined,
      };
      await createPatient.mutateAsync(cleanPatientData);
      onSuccess();
    } catch (error) {
      console.error('Error creating patient:', error);
    }
  };

  const isLoading = createOwner.isPending || createPatient.isPending;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Selector de tipo de propietario */}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setOwnerType('existing')}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${
            ownerType === 'existing'
              ? 'border-primary-500 bg-primary-50 text-primary-700'
              : 'border-gray-200 hover:border-gray-300'
          }`}
        >
          <Users className="h-5 w-5" />
          Propietario Existente
        </button>
        <button
          type="button"
          onClick={() => {
            setOwnerType('new');
            setSelectedOwnerId(null);
          }}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${
            ownerType === 'new'
              ? 'border-primary-500 bg-primary-50 text-primary-700'
              : 'border-gray-200 hover:border-gray-300'
          }`}
        >
          <UserPlus className="h-5 w-5" />
          Nuevo Propietario
        </button>
      </div>

      {/* Sección de propietario */}
      <div className="border rounded-lg p-4 bg-gray-50">
        <h3 className="font-medium text-gray-900 mb-4">
          {ownerType === 'existing' ? 'Seleccionar Propietario' : 'Datos del Nuevo Propietario'}
        </h3>

        {ownerType === 'existing' ? (
          <div className="space-y-3">
            <Input
              placeholder="Buscar por nombre, teléfono o email..."
              value={ownerSearch}
              onChange={(e) => setOwnerSearch(e.target.value)}
            />
            {owners && owners.length > 0 && (
              <div className="max-h-48 overflow-y-auto border rounded-lg bg-white">
                {owners.map((owner) => (
                  <button
                    key={owner.id}
                    type="button"
                    onClick={() => setSelectedOwnerId(owner.id)}
                    className={`w-full text-left px-4 py-3 border-b last:border-b-0 hover:bg-gray-50 transition-colors ${
                      selectedOwnerId === owner.id ? 'bg-primary-50 border-primary-200' : ''
                    }`}
                  >
                    <div className="font-medium text-gray-900">
                      {owner.first_name} {owner.last_name}
                    </div>
                    <div className="text-sm text-gray-500">
                      {owner.phone && `Tel: ${owner.phone}`}
                      {owner.phone && owner.email && ' • '}
                      {owner.email && owner.email}
                    </div>
                  </button>
                ))}
              </div>
            )}
            {ownerSearch && owners?.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-4">
                No se encontraron propietarios.{' '}
                <button
                  type="button"
                  onClick={() => setOwnerType('new')}
                  className="text-primary-600 hover:underline"
                >
                  Crear uno nuevo
                </button>
              </p>
            )}
            {selectedOwnerId && (
              <div className="bg-primary-50 border border-primary-200 rounded-lg px-4 py-2">
                <span className="text-sm text-primary-700">
                  Propietario seleccionado:{' '}
                  <strong>
                    {owners?.find((o) => o.id === selectedOwnerId)?.first_name}{' '}
                    {owners?.find((o) => o.id === selectedOwnerId)?.last_name}
                  </strong>
                </span>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                id="owner_first_name"
                label="Nombre *"
                placeholder="Nombre del propietario"
                {...ownerForm.register('first_name')}
                error={ownerForm.formState.errors.first_name?.message}
              />
              <Input
                id="owner_last_name"
                label="Apellido *"
                placeholder="Apellido del propietario"
                {...ownerForm.register('last_name')}
                error={ownerForm.formState.errors.last_name?.message}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                id="owner_phone"
                label="Teléfono"
                placeholder="Número de teléfono"
                {...ownerForm.register('phone')}
              />
              <Input
                id="owner_email"
                type="email"
                label="Email"
                placeholder="correo@ejemplo.com"
                {...ownerForm.register('email')}
                error={ownerForm.formState.errors.email?.message}
              />
            </div>

            {/* Campos adicionales colapsables */}
            <button
              type="button"
              onClick={() => setShowMoreOwnerFields(!showMoreOwnerFields)}
              className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800"
            >
              {showMoreOwnerFields ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
              {showMoreOwnerFields ? 'Ocultar dirección' : 'Mostrar dirección'}
            </button>

            {showMoreOwnerFields && (
              <div className="space-y-4 pt-2">
                <Input
                  id="owner_address"
                  label="Dirección"
                  placeholder="Dirección completa"
                  {...ownerForm.register('address')}
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Sección de mascota */}
      <div className="border rounded-lg p-4">
        <h3 className="font-medium text-gray-900 mb-4">Datos de la Mascota</h3>

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              id="patient_name"
              label="Nombre *"
              placeholder="Nombre de la mascota"
              {...patientForm.register('name')}
              error={patientForm.formState.errors.name?.message}
            />
            <Select
              id="patient_species"
              label="Especie *"
              options={speciesOptions}
              {...patientForm.register('species')}
              error={patientForm.formState.errors.species?.message}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              id="patient_breed"
              label="Raza"
              placeholder="Ej: Labrador, Siamés..."
              {...patientForm.register('breed')}
            />
            <Select
              id="patient_sex"
              label="Sexo"
              options={sexOptions}
              {...patientForm.register('sex')}
            />
          </div>

          {/* Edad */}
          <div className="grid grid-cols-2 gap-4">
            <Input
              id="patient_age_years"
              type="number"
              min="0"
              label="Edad (años)"
              placeholder="0"
              {...patientForm.register('age_years')}
            />
            <Input
              id="patient_age_months"
              type="number"
              min="0"
              max="11"
              label="Meses"
              placeholder="0"
              {...patientForm.register('age_months')}
            />
          </div>

          {/* Campos adicionales colapsables */}
          <button
            type="button"
            onClick={() => setShowMorePatientFields(!showMorePatientFields)}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800"
          >
            {showMorePatientFields ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
            {showMorePatientFields ? 'Menos campos' : 'Más campos (color, peso, microchip...)'}
          </button>

          {showMorePatientFields && (
            <div className="space-y-4 pt-2">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  id="patient_color"
                  label="Color"
                  placeholder="Ej: Negro, Blanco..."
                  {...patientForm.register('color')}
                />
                <Input
                  id="patient_weight"
                  type="number"
                  step="0.1"
                  label="Peso (kg)"
                  placeholder="Ej: 5.5"
                  {...patientForm.register('weight')}
                />
              </div>

              <Input
                id="patient_microchip"
                label="Número de microchip"
                placeholder="Número de identificación"
                {...patientForm.register('microchip_number')}
              />

              <div>
                <label
                  htmlFor="patient_allergies"
                  className="block text-sm font-medium text-taupe-700 mb-1.5"
                >
                  Alergias
                </label>
                <textarea
                  id="patient_allergies"
                  {...patientForm.register('allergies')}
                  rows={2}
                  className="w-full px-4 py-3 border border-cream-300 rounded-xl focus:ring-2 focus:ring-taupe-400 focus:border-taupe-400 outline-none transition-all text-taupe-800 placeholder-taupe-400"
                  placeholder="Alergias conocidas..."
                />
              </div>

              <div>
                <label
                  htmlFor="patient_notes"
                  className="block text-sm font-medium text-taupe-700 mb-1.5"
                >
                  Notas
                </label>
                <textarea
                  id="patient_notes"
                  {...patientForm.register('notes')}
                  rows={2}
                  className="w-full px-4 py-3 border border-cream-300 rounded-xl focus:ring-2 focus:ring-taupe-400 focus:border-taupe-400 outline-none transition-all text-taupe-800 placeholder-taupe-400"
                  placeholder="Observaciones adicionales..."
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Botón submit */}
      <div className="flex justify-end gap-2 pt-4">
        <Button type="submit" loading={isLoading}>
          Crear Paciente
        </Button>
      </div>
    </form>
  );
}
